/**
 * Meta Pixel + Conversions API Tracking Utility
 * Pixel ID: 1650400809670354
 * 
 * This utility provides functions to track conversion events with:
 * - Browser-side Meta Pixel tracking
 * - Server-side Conversions API integration
 * - Deduplication via event_id
 * 
 * EVENT MAPPING:
 * A) ViewContent - Course exploration/viewing
 * B) InitiateCheckout - Start of checkout intent
 * C) VideoPlay - Custom event for video plays
 * D) Lead - Safety Check only
 * E) Subscribe - Sign up success
 * F) Contact - Contact form submission
 * G) AddPaymentInfo - Stripe Pay click
 * H) Purchase - Confirmed payment only
 */

import { API_URL } from '../config';

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Check if fbq is available and we're in browser (not prerendering)
const isFbqAvailable = () => {
  // Skip during react-snap prerendering
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
    return false;
  }
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

// Check if we should send CAPI events (skip during prerender)
const shouldSendCAPI = () => {
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
    return false;
  }
  return typeof window !== 'undefined';
};

// Generate unique event ID for deduplication
const generateEventId = () => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

// Get user data for Advanced Matching (privacy-safe)
const getUserData = () => {
  const userData = {};
  
  // Get email from localStorage if available (from login/signup)
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      if (parsed.user?.email) {
        // Hash email for privacy (Meta will hash it anyway, but we can send hashed)
        userData.em = parsed.user.email.toLowerCase().trim();
      }
    }
  } catch (e) {
    // Silently fail
  }
  
  // Get client user agent and URL
  if (typeof window !== 'undefined') {
    userData.client_user_agent = navigator.userAgent;
    userData.event_source_url = window.location.href;
  }
  
  return userData;
};

// Send event to Conversions API (server-side)
const sendToConversionsAPI = async (eventName, eventData, eventId) => {
  // Skip during prerendering
  if (!shouldSendCAPI()) {
    return;
  }
  
  try {
    const response = await fetch(`${API_URL}/api/meta/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_data: eventData,
        user_data: getUserData(),
        event_time: Math.floor(Date.now() / 1000)
      })
    });
    
    if (response.ok) {
      console.log(`[Meta CAPI] ${eventName} sent to server`);
    }
  } catch (error) {
    console.log(`[Meta CAPI] Failed to send ${eventName}:`, error.message);
  }
};

// Prevent duplicate events on same page load
const firedEvents = new Set();

const shouldFireEvent = (eventKey) => {
  if (firedEvents.has(eventKey)) {
    console.log(`[Meta Pixel] Skipping duplicate event: ${eventKey}`);
    return false;
  }
  firedEvents.add(eventKey);
  return true;
};

// Reset fired events (call on navigation)
export const resetFiredEvents = () => {
  firedEvents.clear();
};

// Track PageView - with deduplication
let pageViewTracked = new Set();

export const trackPageView = (pathname) => {
  // Skip if already tracked this pathname in this session
  if (pageViewTracked.has(pathname)) {
    console.log(`[Meta Pixel] Skipping duplicate PageView for: ${pathname}`);
    return;
  }
  pageViewTracked.add(pathname);

  const eventId = generateEventId();

  // Browser pixel
  if (isFbqAvailable()) {
    try {
      window.fbq('track', 'PageView', {}, { eventID: eventId });
      console.log(`[Meta Pixel] PageView tracked: ${pathname}`);
    } catch (error) {
      console.log('[Meta Pixel] PageView error:', error.message);
    }
  }

  // Server CAPI (deduplicated via event_id) — improves event match quality
  sendToConversionsAPI('PageView', { page_path: pathname }, eventId);
};

// Reset page view tracking (call on hard navigation)
export const resetPageViewTracking = () => {
  pageViewTracked.clear();
};

// ============================================
// A) VIEW CONTENT - Course exploration/viewing
// ============================================
/**
 * Track ViewContent event - fires on course detail page load
 * 
 * CTAs that trigger this:
 * - Explore courses → destination page
 * - Let's Start → destination page
 * - See how the course covers this → destination page
 * - Start the course → destination page
 * - View All Courses → destination page
 * - View Courses → destination page
 * - Start Privacy and Security Basics → destination page
 * 
 * @param {Object} data - Content data
 * @param {string} data.content_name - Course title
 * @param {string} data.content_id - Stable course ID
 * @param {number} data.value - Course price (0 for free preview)
 */
export const trackViewContent = (data = {}) => {
  const eventKey = `ViewContent_${data.content_id || 'unknown'}`;
  if (!shouldFireEvent(eventKey)) return;
  
  const eventId = generateEventId();
  const eventData = {
    content_type: 'product',
    content_name: data.content_name || '',
    content_ids: data.content_id ? [String(data.content_id)] : [],
    value: typeof data.value === 'number' ? data.value : 0,
    currency: 'AUD'
  };
  
  // Browser Pixel
  if (isFbqAvailable()) {
    window.fbq('track', 'ViewContent', eventData, { eventID: eventId });
    console.log('[Meta Pixel] ViewContent tracked:', eventData);
  }
  
  // Server CAPI
  sendToConversionsAPI('ViewContent', eventData, eventId);
};

// ============================================
// B) INITIATE CHECKOUT - Start of checkout intent
// ============================================
/**
 * Track InitiateCheckout event - fires on checkout button click
 * 
 * CTAs that trigger this:
 * - Start your security reset
 * - Get Started
 * - Start Learning now
 * 
 * @param {Object} data - Checkout data
 * @param {string} data.content_name - Course title
 * @param {string} data.content_id - Stable course ID
 * @param {number} data.value - Course price
 */
export const trackInitiateCheckout = (data = {}) => {
  const eventKey = `InitiateCheckout_${data.content_id || 'unknown'}_${Date.now()}`;
  
  const eventId = generateEventId();
  const value = typeof data.value === 'number' ? data.value : 0;
  
  const eventData = {
    content_type: 'product',
    content_name: data.content_name || '',
    content_ids: data.content_id ? [String(data.content_id)] : [],
    value: value,
    currency: 'AUD',
    num_items: 1,
    contents: [{
      id: String(data.content_id || ''),
      quantity: 1,
      item_price: value
    }]
  };
  
  // Browser Pixel
  if (isFbqAvailable()) {
    window.fbq('track', 'InitiateCheckout', eventData, { eventID: eventId });
    console.log('[Meta Pixel] InitiateCheckout tracked:', eventData);
  }
  
  // Server CAPI
  sendToConversionsAPI('InitiateCheckout', eventData, eventId);
};

// ============================================
// C) VIDEO PLAY - Custom event for video plays
// ============================================
/**
 * Track VideoPlay custom event - fires on video play button click
 * 
 * @param {Object} data - Video data
 * @param {string} data.video_name - Video title/name
 * @param {string} data.video_id - Video ID if available
 * @param {string} data.page - Page where video is played
 * @param {string} data.placement - Placement on page
 */
export const trackVideoPlay = (data = {}) => {
  const eventKey = `VideoPlay_${data.video_id || data.video_name || 'unknown'}`;
  if (!shouldFireEvent(eventKey)) return;
  
  const eventId = generateEventId();
  const eventData = {
    video_name: data.video_name || '',
    video_id: data.video_id || '',
    page: data.page || window.location.pathname,
    placement: data.placement || 'main'
  };
  
  // Browser Pixel (Custom Event)
  if (isFbqAvailable()) {
    window.fbq('trackCustom', 'VideoPlay', eventData, { eventID: eventId });
    console.log('[Meta Pixel] VideoPlay (custom) tracked:', eventData);
  }
  
  // Server CAPI
  sendToConversionsAPI('VideoPlay', eventData, eventId);
};

// ============================================
// D) LEAD - Safety Check only
// ============================================
/**
 * Track Lead event - fires on Safety Check click
 * 
 * CTAs that trigger this:
 * - Start Safety Check
 * 
 * @param {Object} data - Lead data
 * @param {string} data.lead_type - Type of lead (default: safety_check)
 */
export const trackLead = (data = {}) => {
  const eventKey = `Lead_${data.lead_type || 'safety_check'}`;
  if (!shouldFireEvent(eventKey)) return;
  
  const eventId = generateEventId();
  const eventData = {
    lead_type: data.lead_type || 'safety_check',
    content_name: data.content_name || 'Safety Check',
    ...data
  };
  
  // Browser Pixel
  if (isFbqAvailable()) {
    window.fbq('track', 'Lead', eventData, { eventID: eventId });
    console.log('[Meta Pixel] Lead tracked:', eventData);
  }
  
  // Server CAPI
  sendToConversionsAPI('Lead', eventData, eventId);
};

// ============================================
// E) SUBSCRIBE - Sign up success
// ============================================
/**
 * Track Subscribe event - fires on successful signup
 * 
 * CTAs that trigger this:
 * - Sign up (on successful submit)
 * 
 * @param {Object} data - Subscription data
 */
export const trackSubscribe = (data = {}) => {
  const eventKey = 'Subscribe_signup';
  if (!shouldFireEvent(eventKey)) return;
  
  const eventId = generateEventId();
  const eventData = {
    content_name: data.content_name || 'Account Signup',
    status: 'complete',
    ...data
  };
  
  // Browser Pixel
  if (isFbqAvailable()) {
    window.fbq('track', 'Subscribe', eventData, { eventID: eventId });
    console.log('[Meta Pixel] Subscribe tracked:', eventData);
  }
  
  // Server CAPI
  sendToConversionsAPI('Subscribe', eventData, eventId);
};

// ============================================
// F) CONTACT - Contact form submission
// ============================================
/**
 * Track Contact event - fires on contact form submit
 * 
 * CTAs that trigger this:
 * - Contact us (on successful submit or mailto/tel click)
 * 
 * @param {Object} data - Contact data
 * @param {string} data.contact_method - How they contacted (form/email/phone)
 */
export const trackContact = (data = {}) => {
  const eventKey = `Contact_${data.contact_method || 'form'}`;
  if (!shouldFireEvent(eventKey)) return;
  
  const eventId = generateEventId();
  const eventData = {
    content_name: data.content_name || 'Contact Form',
    contact_method: data.contact_method || 'form',
    ...data
  };
  
  // Browser Pixel
  if (isFbqAvailable()) {
    window.fbq('track', 'Contact', eventData, { eventID: eventId });
    console.log('[Meta Pixel] Contact tracked:', eventData);
  }
  
  // Server CAPI
  sendToConversionsAPI('Contact', eventData, eventId);
};

// ============================================
// G) ADD PAYMENT INFO - Stripe Pay click
// ============================================
/**
 * Track AddPaymentInfo event - fires on Stripe Pay button click
 * DO NOT fire Purchase here - only on confirmed payment
 * 
 * @param {Object} data - Payment data
 * @param {string} data.content_name - Course name
 * @param {string} data.content_id - Course ID
 * @param {number} data.value - Amount
 * @param {string} data.payment_method - Payment method (default: card)
 */
export const trackAddPaymentInfo = (data = {}) => {
  const eventKey = `AddPaymentInfo_${data.content_id || 'unknown'}`;
  
  const eventId = generateEventId();
  const eventData = {
    content_type: 'product',
    content_name: data.content_name || '',
    content_ids: data.content_id ? [String(data.content_id)] : [],
    value: typeof data.value === 'number' ? data.value : 0,
    currency: 'AUD',
    payment_method: data.payment_method || 'card'
  };
  
  // Browser Pixel
  if (isFbqAvailable()) {
    window.fbq('track', 'AddPaymentInfo', eventData, { eventID: eventId });
    console.log('[Meta Pixel] AddPaymentInfo tracked:', eventData);
  }
  
  // Server CAPI
  sendToConversionsAPI('AddPaymentInfo', eventData, eventId);
};

// ============================================
// H) PURCHASE - Confirmed payment only
// ============================================
/**
 * Track Purchase event - fires ONLY on confirmed payment
 * 
 * Trigger: Success page after Stripe redirect
 * Server also fires via Stripe webhook for deduplication
 * 
 * @param {Object} data - Purchase data
 * @param {number} data.value - Actual total paid
 * @param {string} data.order_id - Order/transaction ID
 * @param {string} data.content_id - Course ID
 * @param {string} data.content_name - Course name
 * @param {string} data.event_id - Event ID for deduplication (from server if available)
 */
export const trackPurchase = (data = {}) => {
  // Use provided event_id for deduplication with server, or generate new
  const eventId = data.event_id || generateEventId();
  const eventKey = `Purchase_${data.order_id || eventId}`;
  
  // Check if already fired with this order_id
  if (!shouldFireEvent(eventKey)) return;
  
  const value = typeof data.value === 'number' ? data.value : 0;
  
  const eventData = {
    value: value,
    currency: 'AUD',
    content_type: 'product',
    content_name: data.content_name || '',
    content_ids: data.content_id ? [String(data.content_id)] : [],
    contents: [{
      id: String(data.content_id || ''),
      quantity: 1,
      item_price: value
    }],
    num_items: 1,
    order_id: data.order_id || '',
    transaction_id: data.order_id || ''
  };
  
  // Browser Pixel
  if (isFbqAvailable()) {
    window.fbq('track', 'Purchase', eventData, { eventID: eventId });
    console.log('[Meta Pixel] Purchase tracked:', eventData);
  }
  
  // Note: Server CAPI Purchase is sent via Stripe webhook with same event_id
  // We don't send browser Purchase to CAPI to avoid double-sending
};

// ============================================
// LEGACY SUPPORT - CompleteRegistration (use trackSubscribe instead)
// ============================================
export const trackCompleteRegistration = (data = {}) => {
  // Redirect to Subscribe for consistency
  trackSubscribe({
    content_name: data.content_name || 'Account Registration',
    ...data
  });
};

// ============================================
// CUSTOM EVENT - Generic tracker
// ============================================
export const trackCustomEvent = (eventName, data = {}) => {
  const eventId = generateEventId();
  
  if (isFbqAvailable()) {
    window.fbq('trackCustom', eventName, data, { eventID: eventId });
    console.log(`[Meta Pixel] Custom event '${eventName}' tracked:`, data);
  }
  
  sendToConversionsAPI(eventName, data, eventId);
};

// ============================================
// EXPORTS
// ============================================
export default {
  trackViewContent,
  trackInitiateCheckout,
  trackVideoPlay,
  trackLead,
  trackSubscribe,
  trackContact,
  trackAddPaymentInfo,
  trackPurchase,
  trackCompleteRegistration,
  trackCustomEvent,
  resetFiredEvents
};
